import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { PersonalizeItem } from "@shared/schemas";
import type { PublicUser } from "@shared/types";
import { api } from "../lib/api";
import { type CartEntry, clearCart as clearCartStorage, loadCart, makeCartEntry, saveCart } from "../lib/cart";
import { FORMATS } from "@shared/pricing";

/**
 * Typed route descriptor. Mirrors the URL space — keep both in sync with
 * routeToPath() below and the <Route> table in App.tsx.
 */
export type Route =
  | { name: "home" }
  | { name: "detail"; bookId: string }
  | { name: "personalize"; bookId: string }
  | { name: "cart" }
  | { name: "checkout" }
  | { name: "orders" }
  | { name: "confirm"; orderNumber: string };

export function routeToPath(route: Route): string {
  switch (route.name) {
    case "home":
      return "/";
    case "detail":
      return `/books/${encodeURIComponent(route.bookId)}`;
    case "personalize":
      return `/personalize/${encodeURIComponent(route.bookId)}`;
    case "cart":
      return "/cart";
    case "checkout":
      return "/checkout";
    case "orders":
      return "/orders";
    case "confirm":
      return `/orders/${encodeURIComponent(route.orderNumber)}`;
  }
}

type AppContextValue = {
  go: (next: Route) => void;
  cart: CartEntry[];
  addToCart: (item: PersonalizeItem) => CartEntry;
  removeFromCart: (id: string) => void;
  updateCartItem: (id: string, item: PersonalizeItem) => void;
  clearCart: () => void;
  user: PublicUser | null;
  userLoading: boolean;
  requestSignIn: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  signInPanelOpen: boolean;
  openSignInPanel: () => void;
  closeSignInPanel: () => void;
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartEntry[]>(() => loadCart());
  const [user, setUser] = useState<PublicUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [signInPanelOpen, setSignInPanelOpen] = useState(false);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.me();
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const go = useCallback(
    (next: Route) => {
      navigate(routeToPath(next));
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    },
    [navigate]
  );

  const addToCart = useCallback((item: PersonalizeItem) => {
    const entry = makeCartEntry(item);
    setCart((prev) => [...prev, entry]);
    return entry;
  }, []);

  const removeFromCart = useCallback((id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateCartItem = useCallback((id: string, item: PersonalizeItem) => {
    const fmt = FORMATS[item.format];
    setCart((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? { ...item, id, priceCents: fmt.priceCents, formatName: fmt.name }
          : entry
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    clearCartStorage();
  }, []);

  const requestSignIn = useCallback(async (email: string) => {
    await api.requestMagicLink(email);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      go,
      cart,
      addToCart,
      removeFromCart,
      updateCartItem,
      clearCart,
      user,
      userLoading,
      requestSignIn,
      signOut,
      refreshUser,
      signInPanelOpen,
      openSignInPanel: () => setSignInPanelOpen(true),
      closeSignInPanel: () => setSignInPanelOpen(false),
    }),
    [go, cart, addToCart, removeFromCart, updateCartItem, clearCart, user, userLoading, requestSignIn, signOut, refreshUser, signInPanelOpen]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
